import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  TablePagination,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Security,
  Lock,
  LockOpen,
  Warning,
  Error,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  Computer
} from '@mui/icons-material';
import { adminService } from '../../services/adminService';

const SecurityLogs = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [kpi, setKpi] = useState(null);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingKPI, setLoadingKPI] = useState(true);

  // Filtres pour les tentatives de connexion
  const [loginPage, setLoginPage] = useState(0);
  const [loginRowsPerPage, setLoginRowsPerPage] = useState(25);
  const [loginSearch, setLoginSearch] = useState('');
  const [loginSuccessFilter, setLoginSuccessFilter] = useState('');
  const [loginTotal, setLoginTotal] = useState(0);

  // Filtres pour les événements de sécurité
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsRowsPerPage, setEventsRowsPerPage] = useState(25);
  const [eventsSearch, setEventsSearch] = useState('');
  const [eventsSeverityFilter, setEventsSeverityFilter] = useState('');
  const [eventsTotal, setEventsTotal] = useState(0);

  useEffect(() => {
    loadKPI();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      loadLoginAttempts();
    } else if (activeTab === 1) {
      loadSecurityEvents();
    }
  }, [activeTab, loginPage, loginRowsPerPage, loginSearch, loginSuccessFilter, eventsPage, eventsRowsPerPage, eventsSearch, eventsSeverityFilter]);

  const loadKPI = async () => {
    try {
      setLoadingKPI(true);
      const data = await adminService.getSecurityKPI();
      setKpi(data);
    } catch (error) {
      console.error('Error loading security KPI:', error);
    } finally {
      setLoadingKPI(false);
    }
  };

  const loadLoginAttempts = async () => {
    try {
      setLoading(true);
      const params = {
        page: loginPage + 1,
        limit: loginRowsPerPage,
        search: loginSearch || undefined,
        success: loginSuccessFilter || undefined
      };
      const data = await adminService.getLoginAttempts(params);
      setLoginAttempts(data.loginAttempts);
      setLoginTotal(data.pagination.total);
    } catch (error) {
      console.error('Error loading login attempts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSecurityEvents = async () => {
    try {
      setLoading(true);
      const params = {
        page: eventsPage + 1,
        limit: eventsRowsPerPage,
        search: eventsSearch || undefined,
        severity: eventsSeverityFilter || undefined
      };
      const data = await adminService.getSecurityEvents(params);
      setSecurityEvents(data.securityEvents);
      setEventsTotal(data.pagination.total);
    } catch (error) {
      console.error('Error loading security events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        Logs de Sécurité
      </Typography>

      {/* KPI Cards */}
      {loadingKPI ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : kpi && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 3,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 2,
            mb: 4,
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          }}
        >
          {/* Tentatives de connexion */}
          <Card sx={{ minWidth: { xs: 280, sm: 300 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Total Tentatives
              </Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>{kpi.loginAttempts.total.toLocaleString()}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CheckCircle color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2" color="success.main" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {kpi.loginAttempts.successful.toLocaleString()} réussies
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Cancel color="error" sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2" color="error.main" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {kpi.loginAttempts.failed.toLocaleString()} échouées
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Taux de réussite */}
          <Card sx={{ minWidth: { xs: 280, sm: 300 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Taux de Réussite
              </Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>{kpi.loginAttempts.successRate}%</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {kpi.loginAttempts.last24h.failureRate > 20 ? (
                  <TrendingDown color="error" sx={{ fontSize: 16, mr: 0.5 }} />
                ) : (
                  <TrendingUp color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                )}
                <Typography variant="body2" color={kpi.loginAttempts.last24h.failureRate > 20 ? 'error.main' : 'success.main'} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {kpi.loginAttempts.last24h.failureRate}% d'échecs (24h)
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Événements critiques */}
          <Card sx={{ minWidth: { xs: 280, sm: 300 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Événements Critiques
              </Typography>
              <Typography variant="h4" color="error.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                {kpi.securityEvents.bySeverity.critical}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {kpi.securityEvents.criticalLast24h} dans les 24h
              </Typography>
            </CardContent>
          </Card>

          {/* Comptes verrouillés */}
          <Card sx={{ minWidth: { xs: 280, sm: 300 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Comptes Verrouillés
              </Typography>
              <Typography variant="h4" color="warning.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                {kpi.accounts.locked}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {kpi.accounts.locked > 0 ? (
                  <Lock color="warning" sx={{ fontSize: 16, mr: 0.5 }} />
                ) : (
                  <LockOpen color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                )}
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {kpi.accounts.locked > 0 ? 'Action requise' : 'Aucun compte verrouillé'}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* IPs uniques */}
          <Card sx={{ minWidth: { xs: 280, sm: 320 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                IPs Uniques
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{kpi.loginAttempts.uniqueIPs.last24h}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>24 dernières heures</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{kpi.loginAttempts.uniqueIPs.last7Days}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>7 derniers jours</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Top IPs avec échecs */}
          <Card sx={{ minWidth: { xs: 280, sm: 320 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Top IPs avec Échecs (7j)
              </Typography>
              {kpi.loginAttempts.topFailedIPs.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  {kpi.loginAttempts.topFailedIPs.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>{item.ipAddress || 'N/A'}</Typography>
                      <Chip label={item.count} size="small" color="error" />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Aucun échec
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Top utilisateurs avec échecs */}
          <Card sx={{ minWidth: { xs: 280, sm: 320 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Top Utilisateurs avec Échecs (7j)
              </Typography>
              {kpi.loginAttempts.topFailedUsers.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  {kpi.loginAttempts.topFailedUsers.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>{item.email}</Typography>
                      <Chip label={item.count} size="small" color="error" />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Aucun échec
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tabs pour les logs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Tentatives de Connexion" />
          <Tab label="Événements de Sécurité" />
        </Tabs>
      </Box>

      {/* Tentatives de connexion */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Rechercher"
              variant="outlined"
              size="small"
              value={loginSearch}
              onChange={(e) => {
                setLoginSearch(e.target.value);
                setLoginPage(0);
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={loginSuccessFilter}
                label="Statut"
                onChange={(e) => {
                  setLoginSuccessFilter(e.target.value);
                  setLoginPage(0);
                }}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Réussies</MenuItem>
                <MenuItem value="false">Échouées</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>User Agent</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : loginAttempts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">Aucune tentative de connexion</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  loginAttempts.map((attempt) => (
                    <TableRow key={attempt.id}>
                      <TableCell>{formatDate(attempt.createdAt)}</TableCell>
                      <TableCell>{attempt.email}</TableCell>
                      <TableCell>
                        {attempt.user ? (
                          <Chip label={attempt.user.name} size="small" />
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>{attempt.ipAddress || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {attempt.userAgent || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={attempt.success ? 'Réussie' : 'Échouée'}
                          color={attempt.success ? 'success' : 'error'}
                          size="small"
                          icon={attempt.success ? <CheckCircle /> : <Cancel />}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={loginTotal}
            page={loginPage}
            onPageChange={(e, newPage) => setLoginPage(newPage)}
            rowsPerPage={loginRowsPerPage}
            onRowsPerPageChange={(e) => {
              setLoginRowsPerPage(parseInt(e.target.value, 10));
              setLoginPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}

      {/* Événements de sécurité */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Rechercher"
              variant="outlined"
              size="small"
              value={eventsSearch}
              onChange={(e) => {
                setEventsSearch(e.target.value);
                setEventsPage(0);
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sévérité</InputLabel>
              <Select
                value={eventsSeverityFilter}
                label="Sévérité"
                onChange={(e) => {
                  setEventsSeverityFilter(e.target.value);
                  setEventsPage(0);
                }}
              >
                <MenuItem value="">Toutes</MenuItem>
                <MenuItem value="CRITICAL">Critique</MenuItem>
                <MenuItem value="HIGH">Élevée</MenuItem>
                <MenuItem value="MEDIUM">Moyenne</MenuItem>
                <MenuItem value="LOW">Faible</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Sévérité</TableCell>
                  <TableCell>Détails</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : securityEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary">Aucun événement de sécurité</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  securityEvents.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>{formatDate(event.createdAt)}</TableCell>
                      <TableCell>{event.type}</TableCell>
                      <TableCell>
                        {event.user ? (
                          <Chip label={event.user.name || event.userEmail} size="small" />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {event.userEmail || 'N/A'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={event.severity}
                          color={getSeverityColor(event.severity)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {event.details}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={eventsTotal}
            page={eventsPage}
            onPageChange={(e, newPage) => setEventsPage(newPage)}
            rowsPerPage={eventsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setEventsRowsPerPage(parseInt(e.target.value, 10));
              setEventsPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}
    </Box>
  );
};

export default SecurityLogs;

